import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, FileSignature, Package, Upload, Download } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const InvoiceView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [payments, setPayments] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("signed_document");
  const [portalAuth, setPortalAuth] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_method: "credit_card",
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: "",
    notes: ""
  });
  const [deliveryData, setDeliveryData] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    delivery_address: "",
    delivered_by: "",
    received_by: "",
    notes: "",
    items: []
  });

  useEffect(() => {
    fetchInvoice();
    fetchLogo();
    checkAgreement();
    fetchPayments();
    fetchDelivery();
    fetchPortalAuth();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error("Error fetching invoice");
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/logo`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setLogoUrl(url);
    } catch (error) {
      console.log("No logo uploaded yet");
    }
  };

  const checkAgreement = async () => {
    try {
      const response = await axios.get(`${API}/agreements/by-invoice/${id}`);
      setAgreement(response.data);
    } catch (error) {
      // No agreement yet
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/invoice/${id}`);
      setPayments(response.data);
    } catch (error) {
      console.log("No payments yet");
    }
  };

  const fetchDelivery = async () => {
    try {
      const response = await axios.get(`${API}/deliveries/invoice/${id}`);
      setDelivery(response.data);
    } catch (error) {
      console.log("No delivery record yet");
    }
  };

  const fetchPortalAuth = async () => {
    try {
      const response = await axios.get(`${API}/customer-portal/authorization/${id}`);
      if (response.data.signed) {
        setPortalAuth(response.data);
      }
    } catch (error) {
      console.log("No portal authorization yet");
    }
  };

  const initializeDeliveryData = () => {
    if (!invoice) return;
    
    const deliveryItems = invoice.items.map(item => ({
      product_name: item.product_name,
      quantity: item.quantity,
      serial_numbers: Array(item.quantity).fill("")
    }));
    
    setDeliveryData({
      delivery_date: new Date().toISOString().split('T')[0],
      delivery_address: "",
      delivered_by: "",
      received_by: "",
      notes: "",
      items: deliveryItems
    });
    setEditingDelivery(false);
    setShowDeliveryDialog(true);
  };

  const initializeEditDelivery = () => {
    if (!delivery) return;
    
    setDeliveryData({
      delivery_date: delivery.delivery_date ? new Date(delivery.delivery_date).toISOString().split('T')[0] : "",
      delivery_address: delivery.customer_address || "",
      delivered_by: delivery.delivered_by || "",
      received_by: delivery.received_by || "",
      notes: delivery.notes || "",
      items: delivery.items.map(item => ({
        product_name: item.product_name,
        quantity: item.quantity,
        serial_numbers: [...item.serial_numbers]
      }))
    });
    setEditingDelivery(true);
    setShowDeliveryDialog(true);
  };

  const updateSerialNumber = (itemIndex, serialIndex, value) => {
    const newItems = [...deliveryData.items];
    newItems[itemIndex].serial_numbers[serialIndex] = value;
    setDeliveryData({ ...deliveryData, items: newItems });
  };

  const handleSaveDelivery = async () => {
    for (const item of deliveryData.items) {
      if (item.serial_numbers.some(sn => !sn.trim())) {
        toast.error("Please enter all serial numbers");
        return;
      }
    }

    if (!deliveryData.delivered_by.trim()) {
      toast.error("Please enter who delivered the items");
      return;
    }

    const payload = {
      invoice_id: id,
      delivery_address: deliveryData.delivery_address,
      delivery_date: new Date(deliveryData.delivery_date).toISOString(),
      items: deliveryData.items,
      delivered_by: deliveryData.delivered_by,
      received_by: deliveryData.received_by,
      notes: deliveryData.notes
    };

    try {
      if (editingDelivery && delivery) {
        await axios.put(`${API}/deliveries/${delivery.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Delivery record updated successfully");
      } else {
        await axios.post(`${API}/deliveries`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Delivery record created successfully");
      }
      setShowDeliveryDialog(false);
      fetchDelivery();
    } catch (error) {
      toast.error("Error saving delivery record");
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      await axios.post(`${API}/payments`, {
        invoice_id: id,
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        payment_date: new Date(paymentData.payment_date).toISOString(),
        reference_number: paymentData.reference_number,
        notes: paymentData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Payment recorded successfully");
      setShowPaymentDialog(false);
      setPaymentData({
        amount: "",
        payment_method: "credit_card",
        payment_date: new Date().toISOString().split('T')[0],
        reference_number: "",
        notes: ""
      });
      fetchPayments();
      fetchInvoice();
    } catch (error) {
      toast.error("Error recording payment");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, PNG, and PDF files are allowed");
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploadingDocument(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('file_type', documentType);
      
      await axios.post(`${API}/invoices/${id}/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      setDocumentType("signed_document");
      fetchInvoice(); // Refresh to show new document
      
      // Clear file input
      const fileInput = document.getElementById('document-upload-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      toast.error("Error uploading document");
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/documents/${documentId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Document downloaded");
    } catch (error) {
      toast.error("Error downloading document");
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await axios.delete(`${API}/invoices/${id}/documents/${documentId}`);
      toast.success("Document deleted successfully");
      fetchInvoice(); // Refresh to remove deleted document
    } catch (error) {
      toast.error("Error deleting document");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm("Are you sure you want to delete this payment record?")) {
      try {
        await axios.delete(`${API}/payments/${paymentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Payment deleted successfully");
        fetchPayments();
        fetchInvoice();
      } catch (error) {
        toast.error("Error deleting payment");
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintStatement = () => {
    navigate(`/statement/${invoice.id}`);
  };

  if (!invoice) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="invoice-view-title">Invoice Details</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/invoices')} data-testid="back-to-list-btn">
            Back
          </Button>
          {!delivery && (
            <Button onClick={initializeDeliveryData} data-testid="create-delivery-btn">
              <Package className="h-4 w-4 mr-2" />
              Create Delivery
            </Button>
          )}
          {delivery && (
            <>
              <Button variant="outline" onClick={() => navigate(`/delivery/${invoice.id}`)} data-testid="view-delivery-btn">
                <Package className="h-4 w-4 mr-2" />
                View Delivery Form
              </Button>
              <Button variant="outline" onClick={initializeEditDelivery} data-testid="edit-delivery-btn">
                <Pencil className="h-4 w-4 mr-2" />
                Edit Delivery
              </Button>
            </>
          )}
          <Button onClick={handlePrint} data-testid="print-invoice-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print Invoice
          </Button>
          <Button variant="outline" onClick={handlePrintStatement} data-testid="print-statement-btn">
            <FileText className="h-4 w-4 mr-2" />
            Print Statement
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const payUrl = `${window.location.origin}/pay`;
              navigator.clipboard.writeText(payUrl);
              toast.success(`Payment portal link copied! Share with customer: ${payUrl}`);
            }}
            data-testid="copy-payment-link-btn"
          >
            Copy Payment Link
          </Button>
          {agreement && (
            <Button onClick={() => navigate(`/agreements/${invoice.id}`)} data-testid="view-agreement-btn">
              <FileSignature className="h-4 w-4 mr-2" />
              View Agreement
            </Button>
          )}
        </div>
      </div>

      {/* Delivery Dialog */}
      <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle>{editingDelivery ? "Edit Delivery Record" : "Create Delivery Record"}</DialogTitle>
            <DialogDescription>
              Enter serial numbers for delivered items
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delivery Date</Label>
                <Input
                  type="date"
                  value={deliveryData.delivery_date}
                  onChange={(e) => setDeliveryData({ ...deliveryData, delivery_date: e.target.value })}
                  data-testid="delivery-date-input"
                />
              </div>
              <div>
                <Label>Delivered By</Label>
                <Input
                  value={deliveryData.delivered_by}
                  onChange={(e) => setDeliveryData({ ...deliveryData, delivered_by: e.target.value })}
                  placeholder="Delivery personnel name"
                  data-testid="delivered-by-input"
                />
              </div>
            </div>

            <div>
              <Label>Delivery Address</Label>
              <Input
                value={deliveryData.delivery_address}
                onChange={(e) => setDeliveryData({ ...deliveryData, delivery_address: e.target.value })}
                placeholder={invoice ? `Leave blank to use: ${invoice.customer_address}` : "Enter delivery address"}
                data-testid="delivery-address-input"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to use the customer's address from the invoice</p>
            </div>
            
            <div className="space-y-4">
              <Label className="text-lg font-semibold">Items & Serial Numbers</Label>
              {deliveryData.items.map((item, itemIndex) => (
                <Card key={itemIndex}>
                  <CardHeader>
                    <CardTitle className="text-base">{item.product_name} (Qty: {item.quantity})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {item.serial_numbers.map((sn, snIndex) => (
                      <div key={snIndex}>
                        <Label>Serial Number {snIndex + 1}</Label>
                        <Input
                          value={sn}
                          onChange={(e) => updateSerialNumber(itemIndex, snIndex, e.target.value)}
                          placeholder={`Enter serial number ${snIndex + 1}`}
                          data-testid={`serial-${itemIndex}-${snIndex}`}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div>
              <Label>Received By (Optional)</Label>
              <Input
                value={deliveryData.received_by}
                onChange={(e) => setDeliveryData({ ...deliveryData, received_by: e.target.value })}
                placeholder="Customer representative name"
                data-testid="received-by-input"
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={deliveryData.notes}
                onChange={(e) => setDeliveryData({ ...deliveryData, notes: e.target.value })}
                placeholder="Delivery notes or special instructions"
                data-testid="delivery-notes-input"
              />
            </div>

            <DialogFooter>
              <Button onClick={handleSaveDelivery} data-testid="save-delivery-btn">
                {editingDelivery ? "Update Delivery Record" : "Create Delivery Record"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="print:shadow-none" data-testid="invoice-card">
        <CardContent className="p-8">
          <div className="space-y-8 invoice-content">
            {/* Company Header */}
            <div className="flex justify-between items-start border-b-2 pb-6">
              <div>
                {logoUrl && (
                  <img src={logoUrl} alt="Ellantes Logo" className="h-20 mb-4" data-testid="company-logo" />
                )}
                <div className="space-y-1">
                  <div className="text-xl font-bold">Ellantes LLC</div>
                  <div className="text-sm text-gray-600">1309 Coffeen Avenue</div>
                  <div className="text-sm text-gray-600">Sheridan, WY 82801</div>
                  <div className="text-sm text-gray-600">United States</div>
                  <div className="text-sm text-gray-600 mt-2">EIN: 33-3145594</div>
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold mb-4">INVOICE</h2>
                <div className="text-2xl font-bold text-blue-600" data-testid="invoice-number">{invoice.invoice_number}</div>
                <div className="text-sm text-gray-600 mt-2">Date: {new Date(invoice.created_at).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Bill To */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-lg mb-2">Bill To:</h3>
                <div data-testid="customer-info">
                  <div className="font-semibold">{invoice.customer_name}</div>
                  <div>{invoice.customer_address}</div>
                  <div>{invoice.customer_email}</div>
                  <div>{invoice.customer_phone}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-lg mb-2">Payment Terms:</h3>
                  <div data-testid="payment-terms-header">{invoice.payment_terms}</div>
                </div>
                {invoice.sales_agent && (
                  <div>
                    <h3 className="font-bold text-lg mb-2">Sales Agent:</h3>
                    <div className="font-semibold text-blue-600" data-testid="sales-agent-name">{invoice.sales_agent}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Items */}
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="font-medium">{item.product_name}</div>
                        <div className="text-sm text-gray-600">{item.description}</div>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>${item.price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">${item.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span data-testid="invoice-subtotal">${invoice.subtotal.toFixed(2)}</span>
                </div>
                {invoice.discount_amount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>
                      Discount {invoice.discount_type === "percentage" ? `(${invoice.discount_value}%)` : ""}:
                    </span>
                    <span data-testid="invoice-discount">-${invoice.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                {invoice.tax_rate > 0 && (
                  <div className="flex justify-between">
                    <span>Tax ({invoice.tax_rate}%):</span>
                    <span data-testid="invoice-tax">${invoice.tax_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold border-t pt-2">
                  <span>Total:</span>
                  <span data-testid="invoice-total">${invoice.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Plan */}
            {invoice.payment_plan.enabled && (
              <div className="bg-blue-50 p-4 rounded-lg print-keep-together" data-testid="payment-plan-section">
                <h3 className="font-bold text-lg mb-2">Payment Plan</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Down Payment:</div>
                    <div className="font-semibold" data-testid="plan-down-payment">${invoice.payment_plan.down_payment.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Amount Financed:</div>
                    <div className="font-semibold" data-testid="plan-financed">${invoice.payment_plan.total_financed.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Monthly Payment:</div>
                    <div className="font-semibold" data-testid="plan-monthly-payment">${invoice.payment_plan.monthly_payment.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Number of Months:</div>
                    <div className="font-semibold" data-testid="plan-months">{invoice.payment_plan.number_of_months}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">APR:</div>
                    <div className="font-semibold" data-testid="plan-apr">{invoice.payment_plan.apr}%</div>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {invoice.notes && (
              <div>
                <h3 className="font-bold mb-2">Notes:</h3>
                <p data-testid="invoice-notes">{invoice.notes}</p>
              </div>
            )}

            {/* Payment Information - Only visible when printed */}
            <div className="hidden print:block mt-8 p-6 bg-gray-50 border rounded-lg print-keep-together">
              <h3 className="font-bold text-lg mb-4">PAYMENT INFORMATION</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Zelle Payment</h4>
                  <p className="text-sm">Email: Ellantesmarketing@gmail.com</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Bank Transfer (ACH)</h4>
                  <p className="text-sm">Routing Number: 021000021</p>
                  <p className="text-sm">Account Number: 2900199576</p>
                </div>
              </div>
            </div>

            {/* Credit Card Authorization - Visible to Admin */}
            {invoice.cc_authorization && invoice.cc_authorization.cardholder_name && (
              <div className="mt-8 pt-6 border-t-2 border-blue-300 print-keep-together" data-testid="cc-auth-admin-section">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  CREDIT CARD ON FILE
                  {portalAuth && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-normal">E-Signed</span>
                  )}
                </h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Cardholder Name</div>
                      <div className="font-bold text-lg mt-1" data-testid="admin-cc-name">{invoice.cc_authorization.cardholder_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">ID Number</div>
                      <div className="font-semibold mt-1" data-testid="admin-cc-id">{invoice.cc_authorization.id_number || 'N/A'}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-2">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Card Number</div>
                      <div className="font-mono font-bold text-lg mt-1" data-testid="admin-cc-number">{invoice.cc_authorization.card_number}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">CVV</div>
                      <div className="font-mono font-bold text-lg mt-1" data-testid="admin-cc-cvv">{invoice.cc_authorization.cvv}</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Expiration Date</div>
                      <div className="font-semibold mt-1" data-testid="admin-cc-expiry">{invoice.cc_authorization.expiration_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Billing ZIP Code</div>
                      <div className="font-semibold mt-1" data-testid="admin-cc-zip">{invoice.cc_authorization.billing_zip}</div>
                    </div>
                  </div>

                  {invoice.cc_authorization.authorization_date && (
                    <div className="text-xs text-gray-500 pt-2 border-t">
                      Authorization Date: {new Date(invoice.cc_authorization.authorization_date).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* E-Signature Record */}
                {portalAuth && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="esignature-record">
                    <h4 className="font-bold text-sm text-green-800 mb-2">ONLINE AUTHORIZATION E-SIGNATURE</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Signed By:</span>
                        <span className="font-bold ml-2" data-testid="esig-name">{portalAuth.signature_name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Card Last 4:</span>
                        <span className="font-mono font-bold ml-2">****{portalAuth.card_last_four}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Signed At:</span>
                        <span className="font-semibold ml-2">{new Date(portalAuth.signed_at).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Customer:</span>
                        <span className="font-semibold ml-2">{portalAuth.customer_name}</span>
                      </div>
                    </div>
                    <p className="text-xs text-green-700 mt-3 italic">Customer authorized recurring monthly payments via the online payment portal.</p>
                  </div>
                )}

                {/* Monthly Payment Amount for easy reference */}
                {(invoice.payment_plan.enabled || (invoice.lease_plan && invoice.lease_plan.enabled)) && (
                  <div className="mt-4 bg-gray-100 rounded-lg p-4 flex items-center justify-between" data-testid="charge-amount-info">
                    <div>
                      <div className="text-sm text-gray-600">Monthly Charge Amount</div>
                      <div className="text-xs text-gray-500">Due on the 15th of each month</div>
                    </div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="admin-monthly-charge">
                      ${invoice.payment_plan.enabled 
                        ? invoice.payment_plan.monthly_payment.toFixed(2) 
                        : invoice.lease_plan.monthly_payment.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No CC on file notice */}
            {(!invoice.cc_authorization || !invoice.cc_authorization.cardholder_name) && 
              (invoice.payment_plan.enabled || (invoice.lease_plan && invoice.lease_plan.enabled)) && (
              <div className="mt-8 pt-6 border-t print:hidden" data-testid="no-cc-notice">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-yellow-800">No Credit Card on File</p>
                    <p className="text-sm text-yellow-700">Share the payment portal link with the customer to collect CC authorization online.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const payUrl = `${window.location.origin}/pay`;
                      navigator.clipboard.writeText(payUrl);
                      toast.success("Payment portal link copied!");
                    }}
                    data-testid="copy-pay-link-no-cc"
                  >
                    Copy Payment Link
                  </Button>
                </div>
              </div>
            )}

            {/* Wet Signature Section - Only visible when printed */}
            <div className="hidden print:block mt-12 pt-8 border-t-2 border-gray-800 signature-section">
              <h3 className="font-bold text-lg mb-6">ACCEPTANCE OF AGREEMENT</h3>
              <p className="mb-8 text-sm leading-relaxed">
                By signing below Purchaser is representing to Ellantes LLC that it has the requisite authority to execute and deliver this Customer Purchase Agreement and is entering into a binding agreement for the purchase of the product and/or services described above and has read and accepts the Ellantes terms and conditions of sale, which may be attached hereto.
              </p>
              <div className="grid grid-cols-2 gap-8 mt-8">
                <div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                  <div className="text-sm font-semibold">Purchaser Signature</div>
                </div>
                <div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                  <div className="text-sm font-semibold">Date</div>
                </div>
              </div>
              <div className="mt-8">
                <div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div>
                <div className="text-sm font-semibold">Print Name</div>
              </div>
              <div className="mt-8">
                <div className="border-b-2 border-gray-800 mb-2 h-12 w-2/3"></div>
                <div className="text-sm font-semibold">Title/Position</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Recording Section - Not visible when printed */}
      <Card className="print:hidden" data-testid="payment-recording-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Payment Records</CardTitle>
          <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
            <DialogTrigger asChild>
              <Button data-testid="record-payment-btn">
                <Plus className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="payment-dialog">
              <DialogHeader>
                <DialogTitle>Record Manual Payment</DialogTitle>
                <DialogDescription>
                  Add a payment record for this invoice
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Payment Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select
                    value={paymentData.payment_method}
                    onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    data-testid="payment-date-input"
                  />
                </div>
                <div>
                  <Label>Reference Number (Optional)</Label>
                  <Input
                    value={paymentData.reference_number}
                    onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                    placeholder="Check number, transaction ID, etc."
                    data-testid="payment-reference-input"
                  />
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Additional payment details"
                    data-testid="payment-notes-input"
                  />
                </div>
                <DialogFooter>
                  <Button onClick={handleRecordPayment} data-testid="save-payment-btn">
                    Record Payment
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {payments.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                      <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                      <TableCell className="capitalize">{payment.payment_method.replace('_', ' ')}</TableCell>
                      <TableCell className="font-semibold">${payment.amount.toFixed(2)}</TableCell>
                      <TableCell>{payment.reference_number || '-'}</TableCell>
                      <TableCell>{payment.notes || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePayment(payment.id)}
                          data-testid={`delete-payment-${payment.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2 w-64">
                  <div className="flex justify-between">
                    <span>Total Paid:</span>
                    <span className="font-bold">${payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Invoice Total:</span>
                    <span className="font-bold">${invoice.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Balance Due:</span>
                    <span className={`${
                      (invoice.total - payments.reduce((sum, p) => sum + p.amount, 0)) > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      ${(invoice.total - payments.reduce((sum, p) => sum + p.amount, 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No payments recorded yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document Upload Section - Not visible when printed */}
      <Card className="print:hidden" data-testid="document-upload-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload Form */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
            <h3 className="font-semibold mb-4">Upload Documents</h3>
            <div className="space-y-4">
              <div>
                <Label>Document Type</Label>
                <Select
                  value={documentType}
                  onValueChange={setDocumentType}
                >
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="signed_document">Signed Agreement/Invoice</SelectItem>
                    <SelectItem value="customer_id">Customer ID (Driver's License/Passport)</SelectItem>
                    <SelectItem value="cc_authorization">Credit Card Authorization</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Select File</Label>
                <Input
                  id="document-upload-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: JPG, PNG, PDF (Max 10MB)
                </p>
              </div>

              {selectedFile && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded border border-blue-200">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm flex-1">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500">
                    ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
              )}

              <Button 
                onClick={handleUploadDocument} 
                disabled={!selectedFile || uploadingDocument}
                data-testid="upload-btn"
                className="w-full"
              >
                {uploadingDocument ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Uploaded Documents List */}
          <div>
            <h3 className="font-semibold mb-4">Uploaded Documents</h3>
            {invoice.uploaded_documents && invoice.uploaded_documents.length > 0 ? (
              <div className="space-y-2">
                {invoice.uploaded_documents.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{doc.filename}</p>
                        <div className="flex gap-4 text-xs text-gray-500 mt-1">
                          <span className="capitalize">{doc.file_type.replace('_', ' ')}</span>
                          <span>•</span>
                          <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                        data-testid={`download-${doc.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.id)}
                        data-testid={`delete-${doc.id}`}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No documents uploaded yet</p>
                <p className="text-sm mt-1">Upload signed agreements, customer ID, or other documents</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

